import { useRef } from "react";
import { X, ExternalLink } from "lucide-react";
import { Tag } from "./Tag";
import { ImageCarousel } from "./ImageCarousel";
import { useFocusTrap } from "../../hooks/useFocusTrap";

export const ProjectPopup = ({ project, onClose }) => {
  const ref = useRef(null);
  useFocusTrap(ref, !!project, onClose);
  if (!project) return null;
  return (
    <div className="scrim flex items-start md:items-center justify-center overflow-y-auto py-6 px-4" onMouseDown={(e) => e.target === e.currentTarget && onClose()} data-testid="project-popup-scrim">
      <div
        ref={ref}
        role="dialog"
        aria-modal="true"
        aria-labelledby="popup-title"
        data-testid={`project-popup-${project.key}`}
        className="bg-white w-full max-h-[90vh] overflow-y-auto"
        style={{ maxWidth: "min(720px, 92vw)", borderRadius: 16, boxShadow: "0 24px 64px rgba(15,34,64,0.35)" }}
      >
        <div className="p-6 md:p-10">
          <div className="flex items-start justify-between gap-4">
            <div className="flex-1 min-w-0">
              <div className="flex flex-wrap gap-2 mb-4">
                {project.tags.map((t) => (
                  <Tag key={t}>{t}</Tag>
                ))}
              </div>
              <h2 id="popup-title" className="text-h2 mb-3" style={{ color: "var(--navy-700)" }}>
                {project.title}
              </h2>
              <p className="body-sm" style={{ color: "var(--ink-500)" }}>
                {project.meta}
              </p>
            </div>
            <button
              type="button"
              onClick={onClose}
              aria-label="Close project details"
              data-testid="project-popup-close"
              className="shrink-0 flex items-center justify-center"
              style={{ width: 40, height: 40, borderRadius: 8, border: "1px solid var(--border-default)", background: "#fff", cursor: "pointer", color: "var(--ink-700)" }}
            >
              <X size={20} aria-hidden="true" />
            </button>
          </div>

          {project.images.length > 0 && (
            <div className="mt-8" data-testid="popup-image-strip">
              <ImageCarousel images={project.images} />
            </div>
          )}

          {project.brief && (
            <p className="body-default mt-8 measure" data-testid="popup-brief">
              {project.brief}
            </p>
          )}

          {project.note && (
            <p className="body-sm mt-4 measure" style={{ color: "var(--ink-500)" }} data-testid="popup-note">
              {project.note}
            </p>
          )}

          {project.videos.length > 0 && (
            <div className="mt-8" data-testid="popup-videos">
              <h3 className="text-h4 mb-4">Videos</h3>
              <ul className="m-0 p-0" style={{ listStyle: "none" }}>
                {project.videos.map((v) => (
                  <li key={v.url} className="flex items-center gap-3 py-3" style={{ borderBottom: "1px solid var(--border-default)" }}>
                    <span aria-hidden="true" className="shrink-0" style={{ width: 10, height: 10, borderRadius: "50%", background: "var(--accent-teal)" }} />
                    <span className="body-sm font-medium" style={{ color: "var(--ink-900)" }}>{v.title}</span>
                    <a href={v.url} target="_blank" rel="noopener noreferrer" className="link-default body-sm ml-auto break-all" data-testid={`popup-video-link-${v.url.slice(-6)}`}>
                      {v.url.replace("https://", "")}
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {project.site && (
            <div className="mt-8">
              <a href={project.site} target="_blank" rel="noopener noreferrer" className="btn btn-secondary" data-testid="popup-site-link">
                {project.siteLabel || "Visit project site"} <ExternalLink size={16} aria-hidden="true" />
              </a>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
